import React from 'react';
import Layout from '../../components/global/layout';
import SEO from '../../components/seo';
import PageBanner from '../../components/layouts/page-banner';
import {
  Box,
  Container,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LightMode from '../../components/global/light-mode';
import VerticalPadding from '../../components/global/vertical-padding';
import { SiteImage } from '../../components/site-image';
import headquartersImage from '../../images/wesleyclover-headquarters.jpg';
import WCLogo from '../../images/svg/wesleyclover-logo-colour.svg';

const useCaseStyles = makeStyles((theme) => ({
  root: {
    '& p': {
      lineHeight: 1.5,
    },
  },
  img: {
    height: 'auto',
    marginTop: 50,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      maxWidth: 300,
    },
    [theme.breakpoints.only('lg')]: {
      maxWidth: 500,
    },
  },
}));

const WesleyClover: React.FC = () => {
  const useCaseStyle = useCaseStyles();

  return (
    <Layout>
      <SEO
        title="Wesley Clover International Optimizes Web Operations"
        description="Wesley Clover relies on Marvel &amp; Snap to take all its websites from concept to reality, and for ongoing, cost-effective web operations."
      />
      <PageBanner
        heading="Wesley Clover International Optimizes Web Operations"
        maxWidth={1200}
      />
      <LightMode>
        <VerticalPadding>
          <Container style={{ overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5} style={{ textAlign: 'center' }}>
                <img
                  src={WCLogo}
                  alt="Wesley Clover logo"
                  width="600"
                  height="158"
                  className={useCaseStyle.img}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  component="p"
                  className="lead"
                  gutterBottom={true}
                >
                  At{' '}
                  <Link
                    href="https://www.wesleyclover.com"
                    target="blank"
                    rel="noopener"
                  >
                    Wesley Clover International
                  </Link>{' '}
                  websites are key to establishing and maintaining a presence in
                  multiple industries and markets. With multiple websites
                  positioning a variety of businesses in the investment
                  management firm’s portfolio, time to launch, quality,
                  reliability, and efficiency are prime considerations in the
                  web marketing process. Wesley Clover relies on Marvel &amp;
                  Snap to take all its websites from concept to reality, and for
                  ongoing, cost-effective web operations.
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Challenge
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Wesley Clover International is a private investment
                    management firm and holding company with more than 40 years
                    of success founding or funding information and
                    communications technology companies, as well as commercial
                    and leisure real estate and philanthropic businesses. With a
                    notable track record of more than 100 technology firms
                    founded or funded to date, Wesley Clover has played a role
                    in many successful brands, including Mitel, Newbridge,
                    Solace Systems, ProntoForms, Martello Technologies, Tundra,
                    and many more. As part of its process, the company provides
                    marketing guidance where needed, and manages the online
                    presence for many of its portfolio companies under the
                    direction of its vice president of marketing.
                  </p>
                  <p>
                    &quot;We fund, found, and start companies all over the
                    world,&quot; says Steve Langford, vice president of
                    marketing at Wesley Clover International. &quot;In some
                    cases, we’re responsible for creating and maintaining the
                    websites for these companies or group operations. Once a
                    decision is made that we need to go live on a website, there
                    is inherent pressure to get things done quickly. But there
                    is also a need to ensure that there is brand consistency and
                    that we don’t end up with sites that look like they are not
                    related to each other. Adhering to standards and making sure
                    that these sites are regionalized is important. Making sure
                    features, functions, and navigation follow best practices is
                    also important.
                  </p>
                  <p>
                    &quot;But because we are not a typical company that sells
                    products, we don’t have a marketing department. Everything
                    we do in the marketing area is outsourced and delegated,
                    including all of our web operations. So, we need to work
                    with someone who can deliver on all these things.&quot;
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
      <Box bgcolor="primary.main" textAlign="center">
        <VerticalPadding>
          <Container>
            <Grid container justifyContent="center">
              <Grid item xs={12} lg={11} xl={10} style={{ color: 'white' }}>
                <Typography
                  variant="h2"
                  component="blockquote"
                  style={{ fontFamily: 'Visby CF', textTransform: 'unset' }}
                >
                  &ldquo;With Marvel &amp; Snap, I know I have access to the
                  latest skills for anything web-related.&rdquo;
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </Box>
      <LightMode>
        <VerticalPadding>
          <Container style={{ lineHeight: 1.77, overflow: 'hidden' }}>
            <Grid container spacing={5} justifyContent="space-between">
              <Grid item xs={12} md={6} lg={5}>
                <SiteImage
                  src={headquartersImage}
                  objectFit="cover"
                  alt="Wesley Clover Headquarters"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 20 }}
                >
                  Solution
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    For more than seven years, Wesley Clover has trusted Marvel
                    &amp; Snap with the development and maintenance of websites
                    for its portfolio. Today, Marvel &amp; Snap manages 10 sites
                    under the Wesley Clover umbrella. Steve explains that the
                    decision to go with Marvel &amp; Snap made sense for a
                    variety of marketing and business reasons.
                  </p>
                  <p>
                    &quot;Once upon a time, everyone who was building a
                    marketing department believed that they would eventually
                    have to hire a webmaster to build a website and maintain
                    it,” Steve explains. &quot;But the model that Marvel &amp;
                    Snap brought to the table was particularly attractive. It
                    offered the opportunity to hire a skilled partner for a set
                    of web management services at a pre-determined price per
                    month. This provided a cost-effective way for me to be
                    confident that our websites would be in good hands without
                    having to hire a team of people to build them and maintain
                    them.
                  </p>
                  <p>
                    &quot;With Marvel &amp; Snap, I know I have access to the
                    latest skills for anything web-related. I don’t have to
                    worry about sending a resource on course to get trained on
                    the latest programming techniques or platform capabilities.
                    I know the team at Marvel &amp; Snap is doing that. So, I
                    can work as a team with them and get the benefits of that
                    knowledge and expertise they bring to the table, and I can
                    sleep at night knowing our web infrastructures are in good
                    hands.”
                  </p>
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  style={{ marginTop: 40 }}
                >
                  Result
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  className={useCaseStyle.root}
                >
                  <p>
                    Steve explains that Marvel &amp; Snap has made launching
                    sites effortless. He also emphasizes that one of the
                    benefits of working with Marvel &amp; Snap is that regular
                    interactions for web maintenance are seamless. The team is
                    available when needed and the turnaround on requests for all
                    sites in his portfolio is immediate.
                  </p>
                  <p>
                    &quot;Websites are like anything else. They need to be
                    maintained and they get dated. So, we are constantly making
                    changes, adding new products, adjusting content, posting
                    press releases and blogs, and even making changes to the
                    management team pages. It’s just part and parcel of the
                    cadence and everyday life of a business. Marvel &amp; Snap
                    manages all that for us on all our sites quickly and cost
                    effectively. All I have to do is send an email request and
                    it gets done, usually the same day. I don’t have to worry
                    about it.
                  </p>
                  <p>
                    &quot;I have very young startup companies in our portfolio
                    and very large companies, and the Marvel &amp; Snap approach
                    works well for all of them. The team is skilled, responsive,
                    and knowledgeable. They all know what they’re doing, they
                    provide high-quality work, and they understand our business
                    and what we need in our web environment. That’s why they are
                    a trusted partner.”
                  </p>
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
    </Layout>
  );
};

export default WesleyClover;
